// Card #1652 (design §6.1) — the single dispatch point from a composed
// scoreboard envelope (`{ mode, fields, banner }`, card #1648's shape) to
// one of the layout families (SINGLE_ENTRY/RANKED_LIST/BLANK; card #1794
// removed the SUMMARY family along with its two mode types). This supersedes
// RingFollowingBoard (#1637's
// deliberately-minimal, single-mode-type stopgap — see that file's own
// header) as ScoreboardPage's content renderer.
//
// score-api's composeTypedFields returns undefined for the WHOLE composed
// result only when NEITHER the current ride NOR the mode's own on-deck/
// last-completed fallback resolves (#1786; scoreboard-public.js ~938-959).
// useScoreboardPoll.js's setData(result) runs on every successful poll
// (not just the first), so that no-`fields` 200 replaces whatever `data`
// ScoreboardPage was holding and React unmounts the previous render —
// there is no "poll-and-hold" at THIS layer that would keep the last good
// render up.
//
// Card #1798 (Jaybill, 2026-08-20: "NEVER show an error message on the
// scoreboard" — Blank+logo is the UNIVERSAL fallback) supersedes #1652's
// original design here: this component used to render an explicit "No
// active class right now" text state for exactly this case (and for an
// unrecognized modeType). Both now route to BlankLayout instead, via the
// SAME classifyBoardRenderState rule ScoreboardPage.jsx uses via
// `onRenderStatus` + `postScoreboardStatus` (#1798 AC8) — see
// boardRenderState.js's header for why the two share one function. The
// board still never goes fully "blank as in no DOM," it goes
// Blank-the-mode-type, on purpose.
import { classifyBoardRenderState } from "./boardRenderState";
import { MODE_TYPE } from "./modeTypes";
import { LAYOUT_FAMILY, layoutFamilyForModeType } from "./layoutFamilies";
import SingleEntryLayout from "./SingleEntryLayout";
import RankedListLayout from "./RankedListLayout";
import BlankLayout from "./BlankLayout";
import MessageLayout from "./MessageLayout";
import ImageLayout from "./ImageLayout";
import MidRideSimpleLayout from "./MidRideSimpleLayout";
import ClassSummaryLayout from "./ClassSummaryLayout";
import "./ScoreboardContent.css";

export default function ScoreboardContent({ mode, fields, banner, theme, sponsor, slideshow = null }) {
  const modeType = mode?.modeType;
  const family = layoutFamilyForModeType(modeType);
  const renderState = classifyBoardRenderState({ mode, fields });

  // #1798 — unrecognized/removed modeType (AC5), missing/malformed mode
  // config (AC6), and "nothing composed for this mode/context yet" (#1786's
  // no-active-class case, AC1) all resolve here now: BlankLayout, the same
  // universal fallback the Blank mode type itself uses. `mode?.logoAssetUrl`
  // (card #2308: score-api's resolved, org-scoped URL, never a hardcoded
  // key) is whatever the mode carries (possibly nothing, possibly garbage
  // from a malformed config) — BlankLayout/resolveBlankLogoAsset never
  // trusts it, same contract as the Blank mode type's own dispatch below.
  // Card #2311 (PROD HOTFIX) — `theme` is passed through alongside it so
  // BlankLayout's fallback chain (stored pick -> theme logo -> NOTHING) has
  // a theme logo to fall back to on every one of these Blank surfaces, not
  // only the literal Blank mode type.
  if (renderState.blank) {
    return (
      <div className="scoreboard-content" data-testid="scoreboard-content">
        <BlankLayout logoAssetUrl={mode?.logoAssetUrl} theme={theme} />
      </div>
    );
  }

  // #1798 — exhaustive by construction, not just by today's coverage: the
  // ternaries above only cover the families a real `fields` payload can
  // still reach at this point (BLANK/UNKNOWN_MODE/NO_ACTIVE_CLASS already
  // returned above via renderState.blank). Every current LAYOUT_FAMILY
  // member (card #1794 removed SUMMARY along with its two mode types) is
  // handled today, so this else is dead code right now — but without it, a
  // new family added to layoutFamilies.js later would fall through every
  // ternary to an empty `.scoreboard-content` div: no layout, no logo,
  // exactly the blank-white-rectangle failure AC1 exists to forbid, and
  // classifyBoardRenderState would still report `blank: false` since it
  // only knows the family resolved to something. Falling back to Blank here
  // closes that gap regardless of what boardRenderState does or doesn't
  // know about a future family.
  let layout;
  if (family === LAYOUT_FAMILY.SINGLE_ENTRY) {
    // Card #2283 (p4.html) — banner now also reaches SingleEntryLayout, not
    // just RankedListLayout below. SingleEntryLayout itself decides whether
    // to render it (only RIDE_INTRO_DETAILED carries one — see bannerFor,
    // scoreboard-mode-payload.js), so passing it unconditionally here is
    // harmless for POST_RIDE_SCORE (banner stays undefined for that type).
    layout = <SingleEntryLayout modeType={modeType} fields={fields} banner={banner} />;
  } else if (family === LAYOUT_FAMILY.RANKED_LIST) {
    layout = (
      <RankedListLayout
        fields={fields}
        banner={banner}
        rankMode={modeType === MODE_TYPE.END_OF_CLASS_RESULT ? "placing" : "rank"}
      />
    );
  } else if (family === LAYOUT_FAMILY.MESSAGE) {
    layout = <MessageLayout message={mode?.message} messageLines={mode?.messageLines} />;
  } else if (family === LAYOUT_FAMILY.MID_RIDE_SIMPLE) {
    layout = <MidRideSimpleLayout fields={fields} banner={banner} />;
  } else if (family === LAYOUT_FAMILY.CLASS_SUMMARY) {
    // Card #3098 -- `sponsor` reaches ONLY this layout (its text line is part
    // of both field sets); every other family ignores it, as before.
    layout = <ClassSummaryLayout fields={fields} banner={banner} fieldSet={mode?.fieldSet} sponsor={sponsor} />;
  } else if (family === LAYOUT_FAMILY.IMAGE) {
    layout = (
      <ImageLayout
        imageAssetUrl={mode?.imageAssetUrl}
        caption={mode?.caption}
        logoAssetUrl={mode?.logoAssetUrl}
        theme={theme}
        slide={Boolean(slideshow)}
      />
    );
  } else {
    layout = <BlankLayout logoAssetUrl={mode?.logoAssetUrl} theme={theme} />;
  }

  // Card #1949 driver decision: this wrapper div, not RankedListLayout's own
  // root element, is the actual flex child of .scoreboard-frame__content
  // (ScoreboardFrame.jsx renders {children} straight through, with no
  // wrapping element of its own), so the top-alignment override for AC2/AC3
  // has to live on THIS class, gated to the ranked-list family only, or it
  // silently does nothing (see RankedListLayout.css's own comment on this).
  const contentClassName =
    family === LAYOUT_FAMILY.RANKED_LIST ? "scoreboard-content scoreboard-content--top-aligned" : "scoreboard-content";

  return (
    <div className={contentClassName} data-testid="scoreboard-content">
      {layout}
    </div>
  );
}
